import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, Flag } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchGroupReports, type AdminReport } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A4.21 — Guruh/kanalga qarshi shikoyatlar. GET /admin/groups/:id/reports
 * (target_type in group|channel). Endpoint (lane-A backend patch) deploy
 * bo'lgach jonli — hozircha halol empty-state. Karta → moderatsiya qarori.
 */
export function GroupReports() {
  const t = useT()
  const { id = '' } = useParams()

  const columns = useMemo<AdminColumn<AdminReport>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      {
        key: 'reporter_username',
        header: t.userExtra.by,
        type: 'text',
        width: 150,
        render: (r) =>
          r.reporter_username ? (
            <Link to={`/users/${r.reporter_id}`} className="text-accent hover:underline">
              @{r.reporter_username}
            </Link>
          ) : (
            <Link to={`/users/${r.reporter_id}`} className="text-accent hover:underline">
              #{r.reporter_id}
            </Link>
          ),
      },
      { key: 'category', header: t.mod.category, type: 'text', width: 120 },
      { key: 'reason', header: t.common.reason, type: 'text', minWidth: 200 },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 120,
        options: [
          { value: 'pending', label: t.mod.pending, status: 'pending' },
          { value: 'resolved', label: t.userExtra.resolved, status: 'active' },
          { value: 'dismissed', label: t.userExtra.dismissed, status: 'deleted' },
        ],
      },
      {
        key: 'id',
        header: t.common.actions,
        type: 'text',
        width: 90,
        sortable: false,
        render: (r) => (
          <Link to={`/moderation/${r.id}`} className="text-accent hover:underline">
            {t.userExtra.openReport}
          </Link>
        ),
      },
      { key: 'created_at', header: t.col.time, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchGroupReports(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.groups.reportsTitle}
        screenId="A4.21"
        actions={
          <Link
            to={`/groups/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.groups.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminReport>
        screenId="A4.21"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        emptyState={{ icon: <Flag size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
